import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, AlertTriangle, Clock, CreditCard } from 'lucide-react';
import { Button } from './ui/button';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

const AdExpiryNotification = () => {
  const { isAuthenticated, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [expiringAds, setExpiringAds] = useState([]);
  const [dismissed, setDismissed] = useState(false);
  const [currentAdIndex, setCurrentAdIndex] = useState(0);

  useEffect(() => {
    if (!isAuthenticated || isAdmin) return;

    const fetchExpiringAds = async () => {
      try {
        const response = await api.get('/advertisements/my');
        const ads = response.data || [];
        
        // Filter for ads expiring within 3 days
        const expiring = ads.filter(ad => {
          if (ad.status !== 'active' || !ad.end_date) return false;
          const endDate = new Date(ad.end_date);
          const now = new Date();
          const daysRemaining = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
          return daysRemaining <= 3 && daysRemaining > 0;
        });
        
        setExpiringAds(expiring);
        
        // Check if user has dismissed today
        const dismissedDate = localStorage.getItem('ad_expiry_dismissed_date');
        if (dismissedDate === new Date().toDateString()) {
          setDismissed(true);
        }
      } catch (error) {
        console.error('Failed to fetch ads:', error);
      }
    };

    fetchExpiringAds();
  }, [isAuthenticated, isAdmin]);

  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem('ad_expiry_dismissed_date', new Date().toDateString());
  };

  const handleRenew = () => {
    navigate('/advertising');
    handleDismiss();
  };

  const getDaysRemaining = (endDate) => {
    const end = new Date(endDate);
    const now = new Date();
    return Math.ceil((end - now) / (1000 * 60 * 60 * 24));
  };

  if (dismissed || expiringAds.length === 0 || !isAuthenticated || isAdmin) {
    return null;
  }

  const currentAd = expiringAds[currentAdIndex];
  const daysRemaining = getDaysRemaining(currentAd.end_date);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -100, opacity: 0 }}
        className="fixed top-16 left-0 right-0 z-40 px-4"
        data-testid="ad-expiry-notification"
      >
        <div className="max-w-4xl mx-auto">
          <div className={`rounded-lg shadow-lg border-2 p-4 flex items-center justify-between gap-4 ${
            daysRemaining <= 1 
              ? 'bg-red-50 border-red-300' 
              : 'bg-orange-50 border-orange-300'
          }`}>
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-full ${
                daysRemaining <= 1 ? 'bg-red-100' : 'bg-orange-100'
              }`}>
                <AlertTriangle className={`h-5 w-5 ${
                  daysRemaining <= 1 ? 'text-red-600' : 'text-orange-600'
                }`} />
              </div>
              <div>
                <p className={`font-semibold ${
                  daysRemaining <= 1 ? 'text-red-800' : 'text-orange-800'
                }`}>
                  {daysRemaining <= 1 ? 'Ad Expires Tomorrow!' : `Ad Expiring in ${daysRemaining} Days`}
                </p>
                <p className="text-sm text-slate-600">
                  Your ad "{currentAd.title}" {daysRemaining <= 1 ? 'expires tomorrow' : `expires in ${daysRemaining} days`}. 
                  Renew now to maintain visibility.
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-2 flex-shrink-0">
              {expiringAds.length > 1 && (
                <span className="text-xs text-slate-500">
                  {currentAdIndex + 1} of {expiringAds.length}
                </span>
              )}
              <Button
                size="sm"
                onClick={handleRenew}
                className={daysRemaining <= 1 
                  ? 'bg-red-500 hover:bg-red-600' 
                  : 'bg-orange-500 hover:bg-orange-600'
                }
                data-testid="renew-ad-btn"
              >
                <CreditCard className="h-4 w-4 mr-2" />
                Renew Now
              </Button>
              <button
                onClick={handleDismiss}
                className="p-1 hover:bg-slate-200 rounded"
                data-testid="dismiss-ad-notification"
              >
                <X className="h-5 w-5 text-slate-500" />
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AdExpiryNotification;
